import { $color, $cssVar } from "@/utils/cssVarUtils"
import { sliderAnatomy as parts } from "@chakra-ui/anatomy"
import type {
  PartsStyleFunction,
  StyleFunctionProps,
  SystemStyleFunction,
  SystemStyleObject,
} from "@chakra-ui/theme-tools"
import { orient } from "@chakra-ui/theme-tools"

function thumbOrientation(props: StyleFunctionProps): SystemStyleObject {
  return orient({
    orientation: props.orientation,
    vertical: {
      left: "50%",
      transform: `translateX(-50%)`,
      _active: {
        transform: `translateX(-50%) scale(1.15)`,
      },
    },
    horizontal: {
      top: "50%",
      transform: `translateY(-50%)`,
      _active: {
        transform: `translateY(-50%) scale(1.15)`,
      },
    },
  })
}

const baseStyleContainer: SystemStyleFunction = (props) => {
  const { orientation } = props

  return {
    display: "inline-block",
    position: "relative",
    cursor: "pointer",
    _disabled: {
      opacity: 0.6,
      cursor: "default",
      pointerEvents: "none",
    },
    ...orient({
      orientation,
      vertical: { h: "100%" },
      horizontal: { w: "100%" },
    }),
  }
}

const baseStyleTrack: SystemStyleFunction = (props) => {
  return {
    overflow: "hidden",
    borderRadius: "sm",
    bg: $color('bg-input-normal'),
    _disabled: {
      opacity: 0.4,
    },
  }
}

const baseStyleThumb: SystemStyleFunction = (props) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    outline: 0,
    zIndex: 1,
    borderRadius: "full",
    bg: $color('scene-primary-normal'),
    boxShadow: "none",
    border: '1px solid',
    borderColor: $color('bg-input-normal'),
    transitionProperty: "transform",
    transitionDuration: "normal",
    _focusVisible: {
      boxShadow: "outline"
    },
    _disabled: {
      bg: '',
    },
    _hover: {
      bg: $color('scene-primary-active'),
    },
    ...thumbOrientation(props),
  }
}

const baseStyleFilledTrack: SystemStyleFunction = (props) => {
  return {
    width: "inherit",
    height: "inherit",
    bg: $color('scene-primary-normal'),
  }
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  container: baseStyleContainer(props),
  track: baseStyleTrack(props),
  thumb: baseStyleThumb(props),
  filledTrack: baseStyleFilledTrack(props),
})

const sizeLg: PartsStyleFunction<typeof parts> = (props) => {
  return {
    thumb: { w: "3xl", h: "3xl" },
    track: orient({
      orientation: props.orientation,
      horizontal: { h: "5px" },
      vertical: { w: "5px" },
    }),
  }
}

const sizeMd: PartsStyleFunction<typeof parts> = (props) => {
  return {
    thumb: { w: "xl", h: "xl" },
    track: orient({
      orientation: props.orientation,
      horizontal: { h: "4px" },
      vertical: { w: "4px" },
    }),
  }
}

const sizeSm: PartsStyleFunction<typeof parts> = (props) => {
  return {
    thumb: { w: "md", h: "md" },
    track: orient({
      orientation: props.orientation,
      horizontal: { h: "3px" },
      vertical: { w: "3px" },
    }),
  }
}

const sizeXs: PartsStyleFunction<typeof parts> = (props) => {
  return {
    thumb: { w: "sm", h: "sm" },
    track: orient({
      orientation: props.orientation,
      horizontal: { h: "2px" },
      vertical: { w: "2px" },
    }),
  }
}

const sizes = {
  lg: sizeLg,
  md: sizeMd,
  sm: sizeSm,
  xs: sizeXs,
}

const defaultProps = {
  size: "sm",
}

export const Slider = {
  parts: parts.keys,
  sizes,
  baseStyle,
  defaultProps,
}